/*
 * ⌘K / Ctrl+K: jump to any page or server, or run a common action. Servers load when the palette opens.
 * Areas can add commands to ACTIONS below (keep it short: the palette is for getting places fast).
 */
import { HardDrives, MagnifyingGlass, Plus, QrCode, SignOut, Translate } from '@phosphor-icons/react'
import { Command } from 'cmdk'
import { Dialog } from 'radix-ui'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useSignOut } from '@/api/hooks/auth'
import { useServers } from '@/api/hooks/servers'
import { StatusPill } from '@/components/ui'
import { useSwitchLanguage } from '@/api/hooks/settings'
import { useLocale } from '@/i18n/useLocale'
import { cn } from '@/lib/cn'
import { NAV } from './nav'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function useCommandPalette() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  return { open, setOpen }
}

export function CommandTrigger({ onOpen, className }: { onOpen: () => void; className?: string }) {
  const { t } = useTranslation('shell')
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex h-9 w-full max-w-sm items-center gap-2.5 rounded-xl bg-surface/70 px-3 text-13 text-subtle ring-1 ring-hairline transition-colors ring-inset hover:bg-surface hover:text-muted',
        className,
      )}
    >
      <MagnifyingGlass className="size-4" aria-hidden="true" />
      <span className="flex-1 truncate text-start">{t('command.trigger')}</span>
      <kbd
        dir="ltr"
        className="hidden h-5 items-center rounded-md bg-control px-1.5 font-mono text-[11px] text-muted sm:inline-flex"
      >
        {isMac ? '⌘' : 'Ctrl'} K
      </kbd>
    </button>
  )
}

function Item({
  onSelect,
  icon,
  children,
  hint,
  value,
  keywords,
}: {
  onSelect: () => void
  icon: ReactNode
  children: ReactNode
  hint?: ReactNode
  value: string
  keywords?: string[]
}) {
  return (
    <Command.Item
      value={value}
      keywords={keywords}
      onSelect={onSelect}
      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl px-3 text-14 text-ink outline-none data-[selected=true]:bg-surface-tint [&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:text-subtle"
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint}
    </Command.Item>
  )
}

const groupCls =
  'px-1.5 pb-1.5 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-12 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-subtle'

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('shell')
  const navigate = useNavigate()
  const { locale } = useLocale()
  const switchLanguage = useSwitchLanguage()
  const signOut = useSignOut()
  const servers = useServers({ pageSize: 100 })
  const go = (to: string) => {
    onOpenChange(false)
    navigate(to)
  }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed inset-x-3 top-[12vh] z-50 mx-auto max-w-xl overflow-hidden rounded-2xl bg-surface shadow-float ring-1 ring-card-line outline-none data-[state=open]:animate-pop-in">
          <Dialog.Title className="sr-only">{t('command.title')}</Dialog.Title>
          <Dialog.Description className="sr-only">{t('command.trigger')}</Dialog.Description>
          <Command loop>
            <div className="flex items-center gap-3 border-b border-hairline px-4">
              <MagnifyingGlass className="size-5 text-subtle" aria-hidden="true" />
              <Command.Input
                autoFocus
                placeholder={t('command.placeholder')}
                className="h-14 flex-1 bg-transparent text-15 text-ink outline-none placeholder:text-subtle"
              />
              <kbd className="rounded-md bg-control px-1.5 py-0.5 font-mono text-[11px] text-muted">Esc</kbd>
            </div>
            <Command.List className="max-h-[min(26rem,60vh)] scrollbar-thin overflow-y-auto">
              <Command.Empty className="px-4 py-10 text-center text-14 text-subtle">
                {t('command.empty')}
              </Command.Empty>
              <Command.Group heading={t('command.groups.pages')} className={groupCls}>
                {NAV.map((n) => (
                  <Item
                    key={n.id}
                    value={`nav ${n.id}`}
                    keywords={[t(`nav.${n.id}`), ...(n.keywords ?? [])]}
                    onSelect={() => go(n.to)}
                    icon={<n.icon />}
                  >
                    {t(`nav.${n.id}`)}
                  </Item>
                ))}
              </Command.Group>
              {servers.data?.items.length ? (
                <Command.Group heading={t('command.groups.servers')} className={groupCls}>
                  {servers.data.items.map((s) => (
                    <Item
                      key={s.id}
                      value={`server ${s.id}`}
                      keywords={[s.name, s.hostname, s.ip ?? '']}
                      onSelect={() => go(`/servers/${s.id}`)}
                      icon={<HardDrives />}
                      hint={<StatusPill kind="license" value={s.licenseState} size="sm" />}
                    >
                      <span className="font-medium">{s.name}</span>{' '}
                      <bdi dir="ltr" className="ms-1 font-mono text-12 text-subtle">
                        {s.hostname}
                      </bdi>
                    </Item>
                  ))}
                </Command.Group>
              ) : null}
              <Command.Group heading={t('command.groups.actions')} className={groupCls}>
                <Item
                  value="action add-server"
                  keywords={[t('command.actions.addServer')]}
                  onSelect={() => go('/servers/new')}
                  icon={<Plus />}
                >
                  {t('command.actions.addServer')}
                </Item>
                <Item
                  value="action activate"
                  keywords={[t('command.actions.activate')]}
                  onSelect={() => go('/activate')}
                  icon={<QrCode />}
                >
                  {t('command.actions.activate')}
                </Item>
                <Item
                  value="action language"
                  keywords={['language', 'لغة', 'English', 'العربية']}
                  onSelect={() => {
                    onOpenChange(false)
                    switchLanguage(locale === 'ar' ? 'en' : 'ar')
                  }}
                  icon={<Translate />}
                >
                  {t('language.switchTo')}
                </Item>
                <Item
                  value="action sign-out"
                  keywords={[t('user.signOut')]}
                  onSelect={() => {
                    onOpenChange(false)
                    signOut.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
                  }}
                  icon={<SignOut className="flip-ltr" />}
                >
                  {t('user.signOut')}
                </Item>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
